import { useState, useEffect } from 'react';
import { Filter } from 'lucide-react';
import {
  listCrews,
  getActiveCrewFilter,
  setActiveCrewFilter,
  getCrewName,
  getCrewColor,
} from '../services/CrewService';
import { useAuth } from '../contexts/AuthContext';

/**
 * CrewFilter — drop-in filter UI for any list/calendar page.
 *
 * - For owners/admins/managers: renders a select with "All Crews" + each crew.
 *   The choice persists in localStorage (per-device), shared across every
 *   page that mounts this component.
 * - For employees: renders a non-interactive read-only pill showing THEIR
 *   crew (or nothing if they're not assigned). They can't change scope.
 * - If no crews exist yet, renders nothing — pages stay clean.
 *
 * Pages observe filter changes by listening for the
 * `maidly:crew-filter-changed` window event, OR by passing an `onChange`
 * prop to receive the new crewId directly.
 */
const CrewFilter = ({ onChange, compact = false }) => {
  const { user } = useAuth();
  const [crews, setCrews] = useState([]);
  const [selected, setSelected] = useState(getActiveCrewFilter());

  useEffect(() => {
    setCrews(listCrews());

    const refresh = () => {
      setCrews(listCrews());
      setSelected(getActiveCrewFilter());
    };

    // Sync across tabs / other components that change the filter
    const onFilterChange = (e) => {
      setSelected(e.detail?.crewId || '');
    };
    window.addEventListener('maidly:crew-filter-changed', onFilterChange);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('maidly:crew-filter-changed', onFilterChange);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  // No crews? Don't render anything.
  if (!crews || crews.length === 0) return null;

  // Employee view: locked to their own crew (or nothing if unassigned)
  if (user?.role === 'employee') {
    let myCrewId = '';
    try {
      const employees = JSON.parse(localStorage.getItem('maidly_employees') || '[]');
      const me = employees.find((e) => e.email === user.email || e.id === user.id);
      myCrewId = me?.crewId || '';
    } catch {
      /* ignore */
    }
    if (!myCrewId) return null;
    return (
      <div
        data-testid="crew-filter-locked"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.45rem 0.9rem',
          borderRadius: 999,
          background: '#f3f4f6',
          border: '1px solid #e5e7eb',
          fontSize: '0.85rem',
          color: '#374151',
        }}
        title="You can only see data for your crew"
      >
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: getCrewColor(myCrewId),
            flexShrink: 0,
          }}
        />
        <span style={{ fontWeight: 600 }}>{getCrewName(myCrewId) || 'My Crew'}</span>
      </div>
    );
  }

  const handleChange = (e) => {
    const next = e.target.value;
    setSelected(next);
    setActiveCrewFilter(next);
    onChange?.(next);
  };

  return (
    <div
      data-testid="crew-filter"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
      }}
    >
      {!compact && <Filter size={16} color="#6b7280" />}
      <div style={{ position: 'relative' }}>
        {selected && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: getCrewColor(selected),
              pointerEvents: 'none',
            }}
          />
        )}
        <select
          value={selected}
          onChange={handleChange}
          data-testid="crew-filter-select"
          className="input"
          style={{
            paddingLeft: selected ? '2rem' : '0.75rem',
            paddingRight: '2rem',
            paddingTop: '0.45rem',
            paddingBottom: '0.45rem',
            fontSize: '0.875rem',
            minWidth: 160,
            cursor: 'pointer',
          }}
        >
          <option value="">All Crews</option>
          {crews.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default CrewFilter;
